function isEscapeKey(event) {
  return (
    event?.key === "Escape" ||
    event?.key === "Esc" ||
    event?.code === "Escape" ||
    event?.keyCode === 27
  )
}

/**
 * Inline calendars rendered directly in the modal body should not block modal Esc.
 * They are always visible by design, unlike popover-style picker menus.
 */
function isInlineDatepickerInModal(node) {
  if (!node || typeof node.closest !== "function") return false
  // Use two closest() calls — not all engines treat comma-separated
  // selector lists the same as querySelector; OR is unambiguous.
  return Boolean(
    node.closest(".bulk-schedule-calendar") ||
    node.closest(".bulk-schedule-calendar-wrap")
  )
}

/**
 * True when the element is rendered and visibly on-screen (avoids stale wrappers during close).
 */
function isClearlyVisible(el) {
  if (!el?.getBoundingClientRect || !(el instanceof Element)) return false
  if (el.getAttribute("aria-hidden") === "true") return false
  const style = window.getComputedStyle(el)
  if (style.visibility === "hidden" || style.display === "none") return false
  if (Number.parseFloat(style.opacity || "1") < 0.02) return false
  const rect = el.getBoundingClientRect()
  if (rect.width < 8 || rect.height < 8) return false
  if (rect.bottom < 0 || rect.right < 0) return false
  if (rect.top > window.innerHeight || rect.left > window.innerWidth)
    return false
  return true
}

/** v-calendar: only interactive date popover with rendered panel (not empty wrapper). */
function isVcDatePanelOpen() {
  const inner = document.querySelector(
    ".vc-popover-content-wrapper.is-interactive .vc-popover-content"
  )
  return Boolean(inner && isClearlyVisible(inner))
}

/** @vuepic/vue-datepicker: outer wrap + menu both visible. */
function isVueDpMenuOpen() {
  const wraps = document.querySelectorAll(".dp__outer_menu_wrap")
  for (const wrap of wraps) {
    if (isInlineDatepickerInModal(wrap)) continue
    if (!isClearlyVisible(wrap)) continue
    const menu = wrap.querySelector(".dp__menu")
    if (menu && isClearlyVisible(menu)) return true
  }
  return false
}

/**
 * Use the actual keydown event so Escape is deferred only when it should close a picker first:
 * - focus path runs through an open calendar/time menu, or
 * - a picker panel is visibly open (e.g. focus still on trigger input while popover is shown).
 *
 * This avoids stale `.dp__menu` / wrapper matches that made deferral flicker (1 vs 2 vs 4 Escape presses).
 */
export function shouldDeferModalEscape(event) {
  if (!isEscapeKey(event)) return false

  const path =
    typeof event.composedPath === "function"
      ? event.composedPath()
      : [event.target].filter(Boolean)

  for (const node of path) {
    if (!node || typeof node.closest !== "function") continue

    if (node.closest(".vc-popover-content-wrapper.is-interactive")) {
      const panel = document.querySelector(
        ".vc-popover-content-wrapper.is-interactive .vc-popover-content"
      )
      if (panel && isClearlyVisible(panel)) return true
    }

    if (node.closest(".dp__outer_menu_wrap")) {
      const wrap = node.closest(".dp__outer_menu_wrap")
      if (isInlineDatepickerInModal(wrap)) continue
      const menu = wrap?.querySelector?.(".dp__menu")
      if (wrap && isClearlyVisible(wrap) && menu && isClearlyVisible(menu)) {
        return true
      }
    }
  }

  return isVcDatePanelOpen() || isVueDpMenuOpen()
}
